import { DynamicPrices, IAsk, IBid } from '@/types'
import { IEditionDynamicTrades } from '@/types/IEdition'
import { useCurrencies } from 'contexts'
import { useUserRegion } from 'hooks/useUserRegion'
import { useCallback } from 'react'
import { FilterArtworkHitProps } from 'types'
import {
  pickDynamicPrice,
  pickDynamicPricesFromAlgoliaHit,
  pickDynamicTradesPrices,
  toDollars,
} from 'utils/dynamic-prices'

export type DynamicPricePicker = ReturnType<typeof useDynamicPricePicker>

export function useDynamicPricePicker() {
  const { userRegion } = useUserRegion()
  const { userCurrency } = useCurrencies()

  const _pickDynamicPrice = useCallback(
    (prices: Partial<DynamicPrices> | undefined | null) => {
      if (!userRegion || !userCurrency || !prices) return null

      return toDollars(pickDynamicPrice({ userRegion, userCurrency, prices }))
    },
    [userRegion, userCurrency]
  )

  const fromBidAttributes = useCallback(
    (attributes: IBid['attributes'] | undefined | null) => {
      return _pickDynamicPrice(attributes)
    },
    [_pickDynamicPrice]
  )

  const fromAskAttributes = useCallback(
    (attributes: IAsk['attributes'] | undefined | null) => {
      return _pickDynamicPrice(attributes)
    },
    [_pickDynamicPrice]
  )

  const fromEditionAttributes = useCallback(
    (attributes: IEditionDynamicTrades | undefined | null) => {
      if (!userCurrency || !userRegion || !attributes)
        return {
          lowestAsk: null,
          lowestAskId: null,
          lowestAskGbp: null,
          highestBid: null,
          highestBidId: null,
          highestBidGbp: null,
          userCurrency: null,
          userRegion: null,
        }

      const prices = pickDynamicTradesPrices({
        trades: attributes,
        userCurrency,
        userRegion,
      })

      return {
        ...prices,
        highestBid: toDollars(prices.highestBid),
        highestBidGbp: toDollars(prices.highestBidGbp),
        lowestAsk: toDollars(prices.lowestAsk),
        lowestAskGbp: toDollars(prices.lowestAskGbp),
        userCurrency,
        userRegion,
      }
    },
    [userCurrency, userRegion]
  )

  const fromAlgoliaHit = useCallback(
    (hit: FilterArtworkHitProps) => {
      const { lowestAsk, highestBid } = pickDynamicPricesFromAlgoliaHit({
        hit,
        userCurrency,
        userRegion,
      })

      return {
        lowestAsk: toDollars(lowestAsk),
        highestBid: toDollars(highestBid),
      }
    },
    [userCurrency, userRegion]
  )

  return {
    fromAskAttributes,
    fromBidAttributes,
    fromEditionAttributes,
    fromAlgoliaHit,
    fromPrices: _pickDynamicPrice,
  }
}
